<template>
  <!-- Floats in the bottom right corner: over the right menu when there is
       one, over the content otherwise. The open panel has its own close button. -->
  <transition
    enter-active-class="duration-200 ease-out"
    enter-from-class="scale-50 opacity-0"
    leave-active-class="duration-150 ease-in"
    leave-to-class="scale-50 opacity-0"
  >
    <div
      v-if="!isOpen"
      ref="root"
      :class="{ '2xl:right-3 2xl:bottom-4': besideRightMenu }"
      class="fixed right-6 bottom-6 z-40"
      @mouseenter="showBubble"
      @mouseleave="hideBubbleSoon"
      @focusin="showBubble"
      @focusout="hideBubbleOnFocusOut"
      @keydown.esc="hideBubble(true)"
    >
      <!-- The bubble lists what the assistant suggests on the page (hover,
           focus or tap on the avatar); it stays in the DOM of this wrapper
           so moving to it from the avatar does not close it. -->
      <Float
        :show="bubbleOpen && hasSuggestions"
        placement="top-end"
        strategy="fixed"
        :offset="12"
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        leave-active-class="transition duration-100 ease-in"
        leave-to-class="opacity-0"
      >
        <button
          ref="avatarButton"
          type="button"
          :aria-label="$t('global.ai_assistant.title', 'Assistant IA')"
          :aria-expanded="hasSuggestions ? bubbleOpen : undefined"
          :aria-controls="
            hasSuggestions ? 'ai-assistant-suggestions' : undefined
          "
          class="block squircle-full"
          @click="onAvatarClick"
          @mouseenter="hovered = true"
          @mouseleave="hovered = false"
          @focus="hovered = true"
          @blur="hovered = false"
        >
          <!-- Zoomed inside the button: the bubble is placed against the
               button, which keeps its size and so does not move it -->
          <span
            class="block squircle-full shadow-xl transition"
            :class="{ 'scale-105': engaged }"
          >
            <AiAssistantAvatar
              :expression="expression"
              :playful="!$route.meta.avatarSerious"
              :size="56"
            />
          </span>
        </button>
        <div
          id="ai-assistant-suggestions"
          class="w-72 squircle-2xl border border-gray-200 bg-white p-2 shadow-xl dark:border-gray-700 dark:bg-gray-800"
        >
          <p
            class="px-2 pt-1 pb-2 text-xs font-medium text-gray-500 dark:text-gray-400"
          >
            {{
              $t(
                'global.ai_assistant.suggested_actions.title',
                'Je peux t’aider avec ce document :'
              )
            }}
          </p>
          <ul class="flex flex-col gap-0.5">
            <li v-for="entry in suggestions" :key="entry.id">
              <button
                type="button"
                class="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm text-gray-800 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-gray-100 dark:hover:bg-gray-700"
                :disabled="entry.action.kind === 'ai' && !canAskAssistant"
                @click="run(entry)"
              >
                <FontAwesomeIcon
                  :icon="['fal', entry.icon]"
                  fixed-width
                  class="shrink-0 text-gray-500 dark:text-gray-400"
                />
                <span class="min-w-0 flex-1">{{ entryLabel(entry) }}</span>
                <FontAwesomeIcon
                  v-if="entry.action.kind === 'ai'"
                  :icon="['fal', 'sparkles']"
                  class="shrink-0 text-primary"
                />
              </button>
            </li>
          </ul>
          <button
            type="button"
            class="mt-1 w-full rounded-lg border-t border-gray-100 px-2 pt-2 pb-1 text-left text-xs font-medium text-gray-500 transition-colors hover:text-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:text-gray-100"
            @click="open"
          >
            {{
              $t(
                'global.ai_assistant.suggested_actions.open_chat',
                'Discuter avec l’assistant'
              )
            }}
          </button>
        </div>
      </Float>
    </div>
  </transition>
  <ConfirmModal ref="confirm" />
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { isNavigationFailure } from 'vue-router'
import { Float } from '@headlessui-float/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import AiAssistantAvatar from '@/views/layouts/main-layout/ai-assistant/AiAssistantAvatar.vue'
import { showSendError } from '@/views/layouts/main-layout/ai-assistant/aiAssistantSendErrors'
import { startUserAction } from '@/observability/faro'
import {
  VIEW_HANDLED_ACTIONS,
  recipientRoute,
  suggestedActionEntries
} from '@/views/layouts/main-layout/ai-assistant/aiSuggestedActions'

// Time to cross the gap between the avatar and the bubble
const HIDE_DELAY = 200

export default {
  name: 'AiAssistantLauncher',
  components: { AiAssistantAvatar, ConfirmModal, Float, FontAwesomeIcon },
  props: {
    // Centred in the right menu column (80px) where that menu shows, from 2xl
    besideRightMenu: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      hovered: false,
      bubbleOpen: false,
      hideTimer: null
    }
  },
  computed: {
    ...mapGetters({
      isOpen: 'aiAssistant/isOpen',
      isSending: 'aiAssistant/isSending',
      quota: 'aiAssistant/getQuota',
      suggestedActions: 'aiAssistant/suggestedActions',
      suggestedActionsResource: 'aiAssistant/suggestedActionsResource'
    }),
    // Other screens fetch invoices too (wizard, payments): only the invoice
    // on screen gets suggestions
    suggestions() {
      const resource = this.suggestedActionsResource
      const invoiceId = this.$route.params?.invoicing_id
      if (
        resource?.type !== 'invoice' ||
        invoiceId == null ||
        String(resource.id) !== String(invoiceId)
      ) {
        return []
      }
      return suggestedActionEntries(this.suggestedActions)
    },
    hasSuggestions() {
      return this.suggestions.length > 0
    },
    canAskAssistant() {
      return !this.isSending && !(this.quota?.percentage >= 100)
    },
    // A reply on its way wins over the hover, the hover over the suggestions
    // On the avatar or on its bubble: moving from one to the other keeps it
    // zoomed (the bubble closes, and lets go, after a short delay)
    engaged() {
      return this.hovered || (this.bubbleOpen && this.hasSuggestions)
    },
    expression() {
      if (this.isSending) {
        return 'thinking'
      }
      if (this.engaged) {
        return 'happy'
      }
      return this.hasSuggestions ? 'listening' : 'idle'
    }
  },
  beforeUnmount() {
    clearTimeout(this.hideTimer)
  },
  methods: {
    ...mapActions({
      toggle: 'aiAssistant/toggle',
      openAssistant: 'aiAssistant/open',
      sendMessage: 'aiAssistant/sendMessage',
      fetchInvoiceID: 'invoicing/fetchInvoiceID',
      sendInvoiceViaPeppol: 'company/sendInvoiceViaPeppol',
      checkPeppolInvoiceable: 'company/checkPeppolInvoiceable'
    }),
    ...mapMutations({
      requestSuggestedAction: 'aiAssistant/requestSuggestedAction'
    }),
    open() {
      // The button leaves without a mouseleave: it must not come back happy
      this.hovered = false
      this.hideBubble()
      this.toggle()
    },
    // With suggestions, a tap shows them (no hover on touch screens); the
    // bubble has its own way into the chat
    onAvatarClick() {
      if (this.hasSuggestions) {
        this.showBubble()
      } else {
        this.open()
      }
    },
    showBubble() {
      clearTimeout(this.hideTimer)
      this.bubbleOpen = true
    },
    hideBubbleSoon() {
      clearTimeout(this.hideTimer)
      this.hideTimer = setTimeout(this.hideBubble, HIDE_DELAY)
    },
    hideBubbleOnFocusOut(event) {
      if (!this.$refs.root?.contains(event.relatedTarget)) {
        this.hideBubble()
      }
    },
    hideBubble(refocus = false) {
      clearTimeout(this.hideTimer)
      this.bubbleOpen = false
      if (refocus) {
        this.$refs.avatarButton?.focus()
      }
    },
    entryLabel({ label }) {
      if ('text' in label) {
        return label.text
      }
      const params = Object.fromEntries(
        Object.entries(label.params).map(([name, key]) => [name, this.$t(key)])
      )
      return this.$t(label.key, params)
    },
    run(entry) {
      this.hovered = false
      this.hideBubble()
      const invoiceId = this.suggestedActionsResource.id
      if (entry.action.kind === 'ai') {
        return this.runWithAssistant(entry, invoiceId)
      }
      if (VIEW_HANDLED_ACTIONS.includes(entry.action.key)) {
        return this.runInInvoiceView(entry, invoiceId)
      }
      const { params } = entry.action
      switch (entry.action.key) {
        case 'send_to_peppol':
          return this.sendToPeppol(invoiceId)
        case 'check_peppol_registration':
          return this.checkPeppolRegistration(invoiceId, params)
        case 'complete_recipient_email': {
          const route = recipientRoute(
            params?.recipient_type,
            params?.recipient_id
          )
          return route && this.$router.push(route)
        }
        case 'add_dossier_items':
          return this.$router.push({
            name: 'invoice_item_dossiers_route',
            params: { invoicing_id: invoiceId }
          })
      }
    },
    // The back writes the prompt; the label is what the chat shows as sent
    async runWithAssistant(entry, invoiceId) {
      if (!this.canAskAssistant) {
        return
      }
      this.openAssistant()
      const error = await this.sendMessage({
        text: this.entryLabel(entry),
        suggestedAction: {
          key: entry.action.key,
          resource_type: 'invoice',
          resource_id: String(invoiceId)
        }
      })
      if (!error) {
        return
      }
      // The back explains it in English: the translated toast says it better
      if (error.code === 'stale') {
        showSendError(this, { code: 'stale' })
        this.refreshInvoice(invoiceId)
        return
      }
      showSendError(this, error)
    },
    // Its modal or menu lives in the invoice's information tab
    async runInInvoiceView(entry, invoiceId) {
      if (this.$route.name !== 'invoice_info_route') {
        const failure = await this.$router.push({
          name: 'invoice_info_route',
          params: { invoicing_id: invoiceId }
        })
        // Left pending, the request would open its modal on a later visit
        if (isNavigationFailure(failure)) {
          return
        }
      }
      this.requestSuggestedAction({
        key: entry.action.key,
        params: entry.action.params,
        docType: entry.docType
      })
    },
    async sendToPeppol(invoiceId) {
      const confirmed = await this.$refs.confirm.show({
        title: this.$t(
          'global.ai_assistant.suggested_actions.send_to_peppol_confirm',
          'Envoyer ce document via Peppol ?'
        ),
        okButton: this.$t('button.peppol.send_via_peppol')
      })
      if (!confirmed) {
        return
      }
      // Critical action, like the print page's send button
      startUserAction('invoice.send_peppol')
      // No printSettings: the back uses the agency's defaults, on purpose.
      // A refused send still answers 200, with success: false
      const response = await this.sendInvoiceViaPeppol({
        id: invoiceId,
        params: {}
      }).catch(() => null)
      if (!response) {
        return
      }
      if (response.success) {
        this.$h.toastSuccess(this.$t('toasts.peppol.sent_to_peppol_network'))
        this.refreshInvoice(invoiceId)
      } else {
        this.$h.toastWarning(response.message)
      }
    },
    async checkPeppolRegistration(invoiceId, params) {
      const response = await this.checkPeppolInvoiceable({
        params: {
          model_type: params?.recipient_type,
          model_id: params?.recipient_id,
          company_id: params?.company_id
        }
      }).catch(() => null)
      if (!response) {
        return
      }
      if (response.success) {
        this.$h.toastSuccess(
          this.$t(
            'global.ai_assistant.suggested_actions.peppol_registered',
            'Le destinataire est inscrit sur Peppol.'
          )
        )
        // Its identifiers are saved: the suggestion becomes send_to_peppol
        this.refreshInvoice(invoiceId)
      } else {
        this.$h.toastWarning(response.message)
      }
    },
    refreshInvoice(invoiceId) {
      this.fetchInvoiceID({ id: invoiceId })
    }
  }
}
</script>
